import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Package, Loader2, ChevronDown, ChevronRight } from 'lucide-react';

const formatINR = (price) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(price || 0);

export default function StackBuilderTab({ isAdmin }) {
  const [stacks, setStacks] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedStack, setSelectedStack] = useState(null);
  const [stackName, setStackName] = useState('');
  const [stackEligibleTiers, setStackEligibleTiers] = useState(['annual']);
  const [stackItems, setStackItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, stack: null });
  const [deleting, setDeleting] = useState(false);
  const [expandedStack, setExpandedStack] = useState(null);

  const fetchStacks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/supplements/stacks');
      setStacks(res.data?.stacks || []);
    } catch (error) {
      toast.error('Failed to load stacks');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/supplements/products');
      setProducts(res.data?.products || []);
    } catch (error) {
      // Products may not load if catalog is empty
    }
  }, []);

  useEffect(() => {
    fetchStacks();
  }, [fetchStacks]);

  // Compute total cost from items
  const computeTotalCost = (items) => {
    return items.reduce((sum, item) => {
      const product = products.find(p => p.id === item.product_id);
      if (!product) return sum;
      return sum + (product.cost_price * item.quantity);
    }, 0);
  };

  const openCreateDialog = async () => {
    await fetchProducts();
    setSelectedStack(null);
    setStackName('');
    setStackEligibleTiers(['annual']);
    setStackItems([]);
    setShowDialog(true);
  };

  const openEditDialog = async (stack) => {
    await fetchProducts();
    setSelectedStack(stack);
    setStackName(stack.name || '');
    setStackEligibleTiers(stack.eligible_plan_tiers || ['annual']);
    // Map existing stack items to editable format
    setStackItems(
      (stack.items || []).map(item => ({
        product_id: item.product_id,
        quantity: item.quantity || 1,
      }))
    );
    setShowDialog(true);
  };

  const toggleProduct = (productId) => {
    setStackItems(prev => {
      const exists = prev.find(i => i.product_id === productId);
      if (exists) {
        return prev.filter(i => i.product_id !== productId);
      }
      return [...prev, { product_id: productId, quantity: 1 }];
    });
  };

  const updateQuantity = (productId, quantity) => {
    const qty = Math.max(0.01, Number(quantity) || 0.01);
    setStackItems(prev =>
      prev.map(i => i.product_id === productId ? { ...i, quantity: qty } : i)
    );
  };

  const handleSave = async () => {
    if (!stackName.trim()) { toast.error('Stack name is required'); return; }
    if (stackItems.length === 0) { toast.error('Add at least one product to the stack'); return; }

    setSubmitting(true);
    try {
      const body = {
        name: stackName.trim(),
        items: stackItems.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
        })),
        eligible_plan_tiers: stackEligibleTiers.length > 0 ? stackEligibleTiers : ['annual'],
      };

      if (selectedStack) {
        await axiosInstance.put(`/supplements/stacks/${selectedStack.id}`, body);
        toast.success('Stack updated');
      } else {
        await axiosInstance.post('/supplements/stacks', body);
        toast.success('Stack created');
      }
      setShowDialog(false);
      fetchStacks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save stack');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.stack) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/supplements/stacks/${deleteDialog.stack.id}`);
      toast.success('Stack deactivated');
      setDeleteDialog({ open: false, stack: null });
      fetchStacks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete stack');
    } finally {
      setDeleting(false);
    }
  };

  const totalCost = computeTotalCost(stackItems);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Stack Builder</h2>
          <p className="text-sm text-muted-foreground">{stacks.length} active stacks</p>
        </div>
        {isAdmin && (
          <Button onClick={openCreateDialog} data-testid="create-stack-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Stack
          </Button>
        )}
      </div>

      {/* Stack Table */}
      {stacks.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Stacks Yet</h3>
            <p className="text-muted-foreground">
              {isAdmin ? 'Click "Create Stack" to bundle products into supplement stacks.' : 'No stacks have been created yet.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="text-left px-4 py-3 text-sm font-medium w-8"></th>
                    <th className="text-left px-4 py-3 text-sm font-medium">Name</th>
                    <th className="text-center px-4 py-3 text-sm font-medium">Items</th>
                    <th className="text-right px-4 py-3 text-sm font-medium">Total Cost</th>
                    <th className="text-left px-4 py-3 text-sm font-medium">Created</th>
                    {isAdmin && <th className="text-center px-4 py-3 text-sm font-medium">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {stacks.map((stack) => (
                    <React.Fragment key={stack.id}>
                      <tr
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => setExpandedStack(expandedStack === stack.id ? null : stack.id)}
                        data-testid={`stack-row-${stack.id}`}
                      >
                        <td className="px-4 py-3">
                          {expandedStack === stack.id
                            ? <ChevronDown className="w-4 h-4 text-slate-400" />
                            : <ChevronRight className="w-4 h-4 text-slate-400" />
                          }
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-800">{stack.name}</td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant="outline">{stack.items?.length || 0} products</Badge>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-slate-800">
                          {formatINR(stack.total_cost)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {stack.created_at
                            ? new Date(stack.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                            : '-'
                          }
                        </td>
                        {isAdmin && (
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(stack)}
                                data-testid={`edit-stack-${stack.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteDialog({ open: true, stack })}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-stack-${stack.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                      {/* Expanded Items Row */}
                      {expandedStack === stack.id && (
                        <tr>
                          <td colSpan={isAdmin ? 6 : 5} className="bg-slate-50 px-8 py-3">
                            {stack.items && stack.items.length > 0 ? (
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-slate-500">
                                    <th className="text-left py-1 font-medium">Product</th>
                                    <th className="text-center py-1 font-medium">Quantity</th>
                                    <th className="text-left py-1 font-medium">Unit</th>
                                    <th className="text-right py-1 font-medium">Cost/Unit</th>
                                    <th className="text-right py-1 font-medium">Subtotal</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {stack.items.map((item, idx) => (
                                    <tr key={idx} className="border-t border-slate-200">
                                      <td className="py-1.5">{item.product_name}</td>
                                      <td className="py-1.5 text-center">{item.quantity}</td>
                                      <td className="py-1.5">{item.unit}</td>
                                      <td className="py-1.5 text-right">{formatINR(item.cost_per_unit || 0)}</td>
                                      <td className="py-1.5 text-right font-medium">{formatINR((item.cost_per_unit || 0) * item.quantity)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <p className="text-sm text-slate-500">No items in this stack.</p>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Stack Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedStack ? 'Edit Stack' : 'Create Stack'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Stack Name *</Label>
              <Input
                value={stackName}
                onChange={(e) => setStackName(e.target.value)}
                placeholder="e.g. Annual Member Starter Pack"
                data-testid="stack-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Eligible Plan Tiers</Label>
              <div className="flex gap-3 mt-1">
                {['annual', 'half_yearly'].map(tier => (
                  <label key={tier} className="flex items-center gap-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={stackEligibleTiers.includes(tier)}
                      onChange={(e) => {
                        setStackEligibleTiers(prev =>
                          e.target.checked
                            ? [...prev, tier]
                            : prev.filter(t => t !== tier)
                        );
                      }}
                      className="w-4 h-4"
                    />
                    {tier === 'annual' ? 'Annual (12 months)' : 'Half-Yearly (6 months)'}
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Select Products *</Label>
              {products.length === 0 ? (
                <p className="text-sm text-muted-foreground">No products available. Create products in the Catalog tab first.</p>
              ) : (
                <div className="border rounded-lg max-h-64 overflow-y-auto divide-y">
                  {products.map(product => {
                    const isSelected = stackItems.some(i => i.product_id === product.id);
                    const item = stackItems.find(i => i.product_id === product.id);
                    return (
                      <div key={product.id} className={`flex items-center gap-3 px-3 py-2 ${isSelected ? 'bg-blue-50' : ''}`}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleProduct(product.id)}
                          className="w-4 h-4"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{product.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {formatINR(product.cost_price)} / {product.unit}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="flex items-center gap-2">
                            <Label className="text-xs whitespace-nowrap">Qty:</Label>
                            <Input
                              type="number"
                              min="0.01"
                              step="0.01"
                              value={item?.quantity || 1}
                              onChange={(e) => updateQuantity(product.id, e.target.value)}
                              className="w-20 h-8 text-sm"
                              data-testid={`qty-${product.id}`}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Running Total */}
            {stackItems.length > 0 && (
              <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
                <span className="text-sm font-medium text-slate-600">
                  {stackItems.length} product{stackItems.length !== 1 ? 's' : ''} selected
                </span>
                <span className="text-lg font-bold text-slate-800">
                  Total: {formatINR(totalCost)}
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={submitting} data-testid="save-stack-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, stack: deleteDialog.stack })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate Stack</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              This will deactivate the stack <strong>"{deleteDialog.stack?.name}"</strong>.
              It will no longer be available for new assignments.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, stack: null })}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-stack-btn">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {deleting ? 'Deleting...' : 'Deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
